import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const monthsLabel = {
  th: [
    "มกราคม",
    "กุมภาพันธ์",
    "มีนาคม",
    "เมษายน",
    "พฤษภาคม",
    "มิถุนายน",
    "กรกฎาคม",
    "สิงหาคม",
    "กันยายน",
    "ตุลาคม",
    "พฤศจิกายน",
    "ธันวาคม",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

export default function DropdownDatepicker({
  value = {},
  onChange,
  minAge = 10,
  maxAge = 100,
  mode = 1,
}) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;

  const current = new Date();
  const currentDay = current.getDate();
  const currentMonth = current.getMonth() + 1;
  const currentYear = current.getFullYear();

  const [currentValue, setCurrentValue] = useState({
    day: "",
    month: "",
    year: "",
    ...value,
  });
  const [yearItems, setYearItems] = useState([]);
  const [monthItems, setMonthItems] = useState([]);
  const [dayItems, setDayItems] = useState([]);

  const monthLongValues = monthsLabel[lang] || monthsLabel.en;

  // ปี
  useEffect(() => {
    const max = currentYear - minAge;
    const min = currentYear - maxAge;
    const list = [];
    for (let y = max; y >= min; y--) {
      list.push({ key: y, label: lang === "th" ? y + 543 : y });
    }
    setYearItems(list);
  }, [lang, currentYear, minAge, maxAge]);

    // Month
    useEffect(() => {
      const year = parseInt(currentValue.year);
      let startMonth = 1, endMonth = 12;
      if (year) {
        if (year === currentYear - maxAge) startMonth = currentMonth;
        if (year === currentYear - minAge) endMonth = currentMonth;
      }
      const list = [];
      for (let m = startMonth; m <= endMonth; m++) {
        list.push({ key: m, label: monthLongValues[m - 1] });
      }
      setMonthItems(list);
    }, [currentValue.year, lang]);
  
    // Day
    useEffect(() => {
      const year = parseInt(currentValue.year);
      const month = parseInt(currentValue.month);
  
      let startDay = 1, endDay = 31;
      if (year && month) {
        endDay = new Date(year, month, 0).getDate();
        // ถ้าปี = minAge และเดือน = เดือนปัจจุบัน → วันสูงสุด = currentDay
        if (year === currentYear - minAge && month === currentMonth) {
          endDay = currentDay;
        }
        // ถ้าปี = maxAge และเดือน = เดือนปัจจุบัน → วันต่ำสุด = currentDay
        if (year === currentYear - maxAge && month === currentMonth) {
          startDay = currentDay;
        }
      }
      if (currentValue.day && (currentValue.day < startDay || currentValue.day > endDay)) {
        setCurrentValue((prev) => ({ ...prev, day: '' }));
        onChange && onChange({ ...currentValue, day: '' });
      }
      const list = [];
      for (let d = startDay; d <= endDay; d++) {
        list.push({ key: d, label: d });
      }
      setDayItems(list);
    }, [currentValue.year, currentValue.month]);
  

  useEffect(() => {
    setCurrentValue({
      day: "",
      month: "",
      year: "",
      ...value,
    });
  }, [value]);

  const handleChange = (type, val) => {
    let updated = { ...currentValue, [type]: val };
    // ถ้าเลือกปี/เดือนใหม่ ต้อง reset day ถ้าเกิน maxDay (จะถูก reset ใน useEffect)
    setCurrentValue(updated);
    onChange && onChange(updated);
  };

  // helper สำหรับ disabled + สีเทา
  const selectClass = (disabled) =>
    `w-24 h-11 rounded border px-2 ${
      disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
    }`;

  return (
    <div className="w-full flex justify-between gap-3 xs:gap-1 text-lg font-normal">
      {/* Day */}
      <select
        className={selectClass(mode === 2 || mode === 3)}
        name="day"
        value={currentValue.day || ""}
        disabled={mode === 2 || mode === 3}
        onChange={(e) => handleChange("day", parseInt(e.target.value) || "")}
      >
        <option value="">{t("day")}</option>
        {dayItems.map(({ key, label }) => (
          <option
            key={key}
            value={key}
            // ถ้ายังไม่ได้เลือก month/year ให้เลือกได้ทุกวัน
            // ถ้าเลือก month/year แล้ว disable วันที่เกิน (เช่น กุมภา)
            disabled={
              currentValue.month && currentValue.year
                ? key >
                  new Date(currentValue.year, currentValue.month, 0).getDate()
                : false
            }
          >
            {label}
          </option>
        ))}
      </select>

      {/* Month */}
      <select
        className={`w-32 h-11 rounded border px-2 ${
          mode === 3 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
        }`}
        name="month"
        value={currentValue.month || ""}
        disabled={mode === 3}
        onChange={(e) => handleChange("month", parseInt(e.target.value) || "")}
      >
        <option value="">{t("month")}</option>
        {monthItems.map(({ key, label }) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>

      {/* Year */}
      <select
        className="w-24 h-11 rounded border px-2"
        name="year"
        value={currentValue.year || ""}
        onChange={(e) => handleChange("year", parseInt(e.target.value) || "")}
      >
        <option value="">{t("birth_year")}</option>
        {yearItems.map(({ key, label }) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}
